<template>
	<section v-if="reviews.length" class="main-content review-section">
		<div class="row">
			<div class="columns column12 align-center">
				<h2>{{ header }}</h2>
			</div>
		</div>
		<div class="row">
			<div class="columns column12 review-slider">
				<client-only>
					<carousel :items-to-show="1" class="carousel">
						<slide v-for="review in reviews" :key="review.ID" class="review-slider-content">
							<p>{{ review.content }}</p>

							<div class="source">
								<span class="source-logo" :class="review.source" />
								<div>
									<strong>{{ review.firstName }}</strong>
									<span v-if="review.date">
										{{ $d(new Date(review.date.date.split(' ')[0]), 'short') }}
									</span>
								</div>
							</div>
						</slide>

						<template #addons>
							<pagination />
						</template>
					</carousel>
				</client-only>
			</div>
		</div>
	</section>
</template>

<script setup>
const { locale } = useI18n();
const config = useRuntimeConfig();

defineProps({
	header: { type: String, default: '' },
});

const { data: reviews } = await useWebsiteFetch('reviews', {
	query: { language: locale.value },
	key: `${locale.value}/reviews`,
});

const getReviewTotalAverage = () => {
	const sum = reviews.value
		.filter((review) => review.rating)
		.reduce((total, current) => total + parseInt(current.rating, 10), 0);

	return sum / reviews.value.length;
};

useHead({
	script: [
		{
			type: 'application/ld+json',
			innerHTML: JSON.stringify({
				'@context': 'http://schema.org',
				'@type': 'AggregateRating',
				'@id': `${config.public.siteUrl}#aggregaterating`,
				bestRating: 10,
				ratingValue: getReviewTotalAverage(),
				ratingCount: reviews.value.length,
				itemReviewed: {
					'@id': `${config.public.siteUrl}#hotel`,
				},
			}),
		},
	],
});
</script>

<style lang="scss" scoped>
h2 {
	color: var(--primary-text-color);
}

.review-section {
	background: url('~/assets/images/reviewslider-bg-pattern.jpg') no-repeat center center;
	background-size: cover;
	padding: 100px 0 150px;
}

.review-slider {
	padding: 50px;
	overflow: hidden;
	max-width: 980px;
	margin: 0 auto;
	float: none;

	.review-slider-content {
		flex-direction: column;
		text-align: center;
		font-size: 20px;
		padding-bottom: 34px;

		.source {
			width: 280px;
			display: flex;
			align-items: center;
			justify-content: center;
			flex-flow: row nowrap;
			margin: 20px auto;
			gap: 10px;

			div {
				display: flex;
				flex-flow: column wrap;
				justify-content: flex-start;
				align-items: flex-start;
			}

			span {
				font-size: 16px;
				margin: 0;
			}

			strong {
				margin: 0 0 5px;
				font-size: 18px;
			}

			.source-logo {
				margin: 0;
				width: 30px;
				height: 30px;
				background-size: 30px;

				&.tripadvisor {
					background-image: url('~/assets/images/review-logo-tripadvisor-small.png');
				}

				&.bookingcom {
					background-image: url('~/assets/images/review-logo-bookingcom-small.png');
				}

				&.zoover {
					background-image: url('~/assets/images/review-logo-zoover-small.png');
				}

				&.expedia {
					background-image: url('~/assets/images/review-logo-expedia-small.png');
				}

				&.google {
					background-image: url('~/assets/images/review-logo-google-small.png');
				}
			}
		}
	}
}

/* stylelint-disable selector-class-pattern */
:deep(.carousel__viewport ol),
:deep(.carousel__pagination) {
	margin: 0;
	padding-left: 0;
	line-height: normal;
}

:deep(.carousel__pagination-item) {
	margin: 0 4px;

	button {
		width: 16px;
		height: 16px;
		border: 1px solid var(--primary-text-color);
		background: none;

		&.carousel__pagination-button--active {
			background: var(--primary-color);
			border-color: var(--primary-color);
		}

		&::after {
			display: none;
		}
	}
}
/* stylelint-enable selector-class-pattern */

@media (max-width: 900px) {
	.review-slider {
		padding: 50px 90px;
		overflow: hidden;
	}
}

@media (max-width: 768px) {
	.review-slider {
		padding: 50px 70px;
		overflow: hidden;
	}
}

@media (max-width: 600px) {
	.review-slider {
		padding: 20px;
	}
}
</style>
